/**
 * useHousingScene — premium 3D housing cluster background.
 * Renders a low-poly city of towers, apartments, townhouses, and trees on a
 * circular plinth. Slowly rotates continuously and additionally rotates with
 * scroll. Subtle pointer parallax. Designed to live in a fixed full-viewport
 * canvas behind the marketing landing.
 */
import { useEffect, useRef } from 'react';
import * as THREE from 'three';

export interface HousingSceneState {
  scrollProgress: number; // 0..1
  pointerX: number;       // -1..1
  pointerY: number;       // -1..1
}

export function useHousingScene(
  canvasRef: React.RefObject<HTMLCanvasElement | null>,
  options: { fitToParent?: boolean } = {}
) {
  const stateRef = useRef<HousingSceneState>({ scrollProgress: 0, pointerX: 0, pointerY: 0 });

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const getSize = () => {
      if (options.fitToParent && canvas.parentElement) {
        return {
          w: canvas.parentElement.clientWidth || window.innerWidth,
          h: canvas.parentElement.clientHeight || window.innerHeight,
        };
      }
      return { w: window.innerWidth, h: window.innerHeight };
    };

    const initial = getSize();

    const renderer = new THREE.WebGLRenderer({ canvas, antialias: true, alpha: true });
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.setSize(initial.w, initial.h);
    renderer.setClearColor(0x000000, 0);
    renderer.shadowMap.enabled = true;
    renderer.shadowMap.type = THREE.PCFSoftShadowMap;
    renderer.toneMapping = THREE.ACESFilmicToneMapping;
    renderer.toneMappingExposure = 1.05;

    const scene = new THREE.Scene();
    scene.fog = new THREE.Fog(0xf5f2ea, 18, 42);

    const camera = new THREE.PerspectiveCamera(34, initial.w / initial.h, 0.1, 100);
    camera.position.set(0, 7.5, 22);
    camera.lookAt(0, 1.5, 0);

    // Lights
    scene.add(new THREE.HemisphereLight(0xfff5e2, 0xcdd9d2, 0.55));

    const sun = new THREE.DirectionalLight(0xffeed1, 1.4);
    sun.position.set(8, 12, 6);
    sun.castShadow = true;
    sun.shadow.mapSize.set(2048, 2048);
    sun.shadow.camera.left = -12;
    sun.shadow.camera.right = 12;
    sun.shadow.camera.top = 12;
    sun.shadow.camera.bottom = -12;
    sun.shadow.camera.near = 0.5;
    sun.shadow.camera.far = 40;
    sun.shadow.bias = -0.0005;
    scene.add(sun);

    const rim = new THREE.DirectionalLight(0x0e7c5a, 0.8);
    rim.position.set(-7, 4, -8);
    scene.add(rim);

    const fill = new THREE.PointLight(0xc68a52, 0.6, 25);
    fill.position.set(-4, 5, 6);
    scene.add(fill);

    // Materials
    const matWall      = new THREE.MeshStandardMaterial({ color: 0xede5d5, roughness: 0.85, metalness: 0.05 });
    const matWallWarm  = new THREE.MeshStandardMaterial({ color: 0xd8c5a7, roughness: 0.82, metalness: 0.05 });
    const matWallDeep  = new THREE.MeshStandardMaterial({ color: 0xb59572, roughness: 0.78, metalness: 0.06 });
    const matRoofA     = new THREE.MeshStandardMaterial({ color: 0x1f2624, roughness: 0.6,  metalness: 0.15 });
    const matRoofB     = new THREE.MeshStandardMaterial({ color: 0x2d3a33, roughness: 0.55, metalness: 0.18 });
    const matRoofCop   = new THREE.MeshStandardMaterial({ color: 0xa06a3f, roughness: 0.5,  metalness: 0.4  });
    const matWindow    = new THREE.MeshStandardMaterial({
      color: 0x6fb89a, emissive: 0x0e7c5a, emissiveIntensity: 0.45,
      roughness: 0.25, metalness: 0.7,
    });
    const matWindowWarm = new THREE.MeshStandardMaterial({
      color: 0xe8c896, emissive: 0xd9a45f, emissiveIntensity: 0.35,
      roughness: 0.3, metalness: 0.6,
    });
    const matAccent  = new THREE.MeshStandardMaterial({ color: 0x0e7c5a, roughness: 0.7, metalness: 0.2 });
    const matBase    = new THREE.MeshStandardMaterial({ color: 0x18211d, roughness: 0.5, metalness: 0.3 });
    const matFoliage = new THREE.MeshStandardMaterial({ color: 0x2a5847, roughness: 0.95, metalness: 0 });

    type Side = 'z+' | 'z-' | 'x+' | 'x-';

    function addWindowGrid(
      building: THREE.Object3D,
      w: number, h: number, depth: number,
      opts: {
        rows?: number; cols?: number;
        padTop?: number; padBottom?: number; padSide?: number;
        winW?: number; winH?: number; winMat?: THREE.Material;
        sides?: Side[];
      } = {}
    ) {
      const {
        rows = 4, cols = 3, padTop = 0.6, padBottom = 0.6,
        padSide = 0.35, winW = 0.28, winH = 0.42, winMat = matWindow,
        sides = ['z+', 'z-', 'x+', 'x-'] as Side[],
      } = opts;

      const usableH = h - padTop - padBottom;
      const startY = -h / 2 + padBottom;

      const place = (side: Side) => {
        const usableW = (side === 'z+' || side === 'z-' ? w : depth) - padSide * 2;
        const stepX = usableW / Math.max(cols - 1, 1);
        const stepY = usableH / Math.max(rows - 1, 1);
        for (let r = 0; r < rows; r++) {
          for (let c = 0; c < cols; c++) {
            const x = -((side === 'z+' || side === 'z-' ? w : depth) / 2) + padSide + c * stepX;
            const y = startY + r * stepY;
            const win = new THREE.Mesh(new THREE.BoxGeometry(winW, winH, 0.04), winMat);
            if (side === 'z+') win.position.set(x, y, depth / 2 + 0.001);
            if (side === 'z-') { win.position.set(x, y, -depth / 2 - 0.001); win.rotation.y = Math.PI; }
            if (side === 'x+') { win.position.set(w / 2 + 0.001, y, x); win.rotation.y = Math.PI / 2; }
            if (side === 'x-') { win.position.set(-w / 2 - 0.001, y, x); win.rotation.y = -Math.PI / 2; }
            building.add(win);
          }
        }
      };
      sides.forEach(place);
    }

    function makeTower(o: {
      w?: number; h?: number; d?: number;
      wall?: THREE.Material; roof?: THREE.Material; winMat?: THREE.Material;
      rows?: number; cols?: number;
    } = {}) {
      const { w = 1.6, h = 6, d = 1.6, wall = matWall, roof = matRoofA, winMat = matWindow, rows = 6, cols = 4 } = o;
      const g = new THREE.Group();

      const plinth = new THREE.Mesh(new THREE.BoxGeometry(w + 0.2, 0.3, d + 0.2), matBase);
      plinth.position.y = 0.15;
      plinth.castShadow = true; plinth.receiveShadow = true;
      g.add(plinth);

      const body = new THREE.Mesh(new THREE.BoxGeometry(w, h, d), wall);
      body.position.y = 0.3 + h / 2;
      body.castShadow = true; body.receiveShadow = true;
      g.add(body);
      addWindowGrid(body, w, h, d, { rows, cols, padTop: 0.5, padBottom: 0.5, winMat });

      const cap = new THREE.Mesh(new THREE.BoxGeometry(w + 0.15, 0.18, d + 0.15), roof);
      cap.position.y = 0.3 + h + 0.09;
      cap.castShadow = true;
      g.add(cap);

      const ant = new THREE.Mesh(new THREE.CylinderGeometry(0.02, 0.02, 0.8, 8), matRoofA);
      ant.position.y = 0.3 + h + 0.6;
      g.add(ant);

      return g;
    }

    function makeTownhouse(o: {
      w?: number; h?: number; d?: number;
      wall?: THREE.Material; roof?: THREE.Material;
    } = {}) {
      const { w = 1.6, h = 1.8, d = 1.4, wall = matWallWarm, roof = matRoofCop } = o;
      const g = new THREE.Group();
      const body = new THREE.Mesh(new THREE.BoxGeometry(w, h, d), wall);
      body.position.y = h / 2;
      body.castShadow = true; body.receiveShadow = true;
      g.add(body);

      const roofGeo = new THREE.ConeGeometry(Math.max(w, d) * 0.78, 0.8, 4);
      const roofMesh = new THREE.Mesh(roofGeo, roof);
      roofMesh.position.y = h + 0.4;
      roofMesh.rotation.y = Math.PI / 4;
      roofMesh.scale.set(1, 1, d / w);
      roofMesh.castShadow = true;
      g.add(roofMesh);

      addWindowGrid(body, w, h, d, {
        rows: 2, cols: 2, padTop: 0.35, padBottom: 0.35, winW: 0.32, winH: 0.4,
        winMat: matWindowWarm, sides: ['z+', 'z-'],
      });

      const door = new THREE.Mesh(new THREE.BoxGeometry(0.3, 0.55, 0.04), matAccent);
      door.position.set(0, 0.275, d / 2 + 0.001);
      g.add(door);

      const chim = new THREE.Mesh(new THREE.BoxGeometry(0.18, 0.5, 0.18), matWallDeep);
      chim.position.set(w * 0.3, h + 0.45, -d * 0.1);
      chim.castShadow = true;
      g.add(chim);

      return g;
    }

    function makeApartmentBlock(o: { w?: number; h?: number; d?: number } = {}) {
      const { w = 2.2, h = 3.2, d = 2.0 } = o;
      const g = new THREE.Group();
      const body = new THREE.Mesh(new THREE.BoxGeometry(w, h, d), matWall);
      body.position.y = h / 2;
      body.castShadow = true; body.receiveShadow = true;
      g.add(body);

      const tierH = 1.4;
      const tier = new THREE.Mesh(new THREE.BoxGeometry(w * 0.7, tierH, d * 0.7), matWallWarm);
      tier.position.y = h + tierH / 2;
      tier.position.x = -w * 0.1;
      tier.castShadow = true;
      g.add(tier);

      addWindowGrid(body, w, h, d, { rows: 4, cols: 4, padTop: 0.3, padBottom: 0.3, winMat: matWindow, winW: 0.22, winH: 0.32 });
      addWindowGrid(tier, w * 0.7, tierH, d * 0.7, { rows: 2, cols: 2, padTop: 0.25, padBottom: 0.25, winW: 0.2, winH: 0.3, winMat: matWindowWarm });

      const acc = new THREE.Mesh(new THREE.BoxGeometry(w * 0.7 + 0.1, 0.05, d * 0.7 + 0.1), matAccent);
      acc.position.y = h + tierH + 0.025;
      acc.position.x = -w * 0.1;
      g.add(acc);

      return g;
    }

    function makeTree(scale = 1) {
      const g = new THREE.Group();
      const trunk = new THREE.Mesh(
        new THREE.CylinderGeometry(0.06, 0.08, 0.5, 6),
        new THREE.MeshStandardMaterial({ color: 0x4a3826, roughness: 0.9 })
      );
      trunk.position.y = 0.25;
      g.add(trunk);
      const top = new THREE.Mesh(new THREE.IcosahedronGeometry(0.32, 0), matFoliage);
      top.position.y = 0.6;
      top.castShadow = true;
      g.add(top);
      g.scale.setScalar(scale);
      return g;
    }

    // City group
    const city = new THREE.Group();
    scene.add(city);

    const sitePlinth = new THREE.Mesh(
      new THREE.CylinderGeometry(7, 7.4, 0.4, 64),
      new THREE.MeshStandardMaterial({ color: 0x2a2f2c, roughness: 0.7, metalness: 0.2 })
    );
    sitePlinth.position.y = -0.2;
    sitePlinth.receiveShadow = true;
    city.add(sitePlinth);

    const pad = new THREE.Mesh(
      new THREE.CylinderGeometry(6.6, 6.6, 0.05, 64),
      new THREE.MeshStandardMaterial({ color: 0x3b4541, roughness: 0.85 })
    );
    pad.position.y = 0.025;
    pad.receiveShadow = true;
    city.add(pad);

    const placements: Array<[() => THREE.Group, number, number, number]> = [
      [() => makeTower({ w: 1.5, h: 5.5, d: 1.5, wall: matWall, roof: matRoofA, rows: 5, cols: 3 }), -1.6, -1.4, 0.3],
      [() => makeTower({ w: 1.3, h: 7.2, d: 1.3, wall: matWallWarm, roof: matRoofCop, rows: 7, cols: 3, winMat: matWindowWarm }), 1.4, -2.0, -0.4],
      [() => makeApartmentBlock({ w: 2.2, h: 3.2, d: 2.0 }), -2.8, 1.6, 0.6],
      [() => makeApartmentBlock({ w: 1.8, h: 2.6, d: 1.8 }), 2.4, 1.9, -0.8],
      [() => makeTownhouse({ w: 1.4, h: 1.6, d: 1.2, wall: matWallWarm, roof: matRoofCop }), 0.2, 2.6, 0.0],
      [() => makeTownhouse({ w: 1.4, h: 1.6, d: 1.2, wall: matWallDeep, roof: matRoofB }), -1.4, 3.4, 0.4],
      [() => makeTownhouse({ w: 1.4, h: 1.6, d: 1.2, wall: matWall, roof: matRoofCop }), 1.6, 3.6, -0.3],
      [() => makeTower({ w: 1.0, h: 4.2, d: 1.0, wall: matWallDeep, roof: matRoofB, rows: 4, cols: 2 }), -3.4, -2.6, 0.2],
      [() => makeTower({ w: 1.1, h: 3.6, d: 1.1, wall: matWall, roof: matRoofA, rows: 3, cols: 2, winMat: matWindowWarm }), 3.2, -3.2, -0.5],
      [() => makeTownhouse({ w: 1.2, h: 1.4, d: 1.0, wall: matWallWarm, roof: matRoofCop }), 3.4, 0.2, -0.6],
      [() => makeTownhouse({ w: 1.2, h: 1.4, d: 1.0, wall: matWall, roof: matRoofA }), -3.6, 0.4, 0.5],
    ];
    placements.forEach(([f, x, z, ry]) => {
      const m = f();
      m.position.set(x, 0, z);
      m.rotation.y = ry;
      city.add(m);
    });

    for (let i = 0; i < 14; i++) {
      const t = makeTree(0.8 + Math.random() * 0.5);
      const a = Math.random() * Math.PI * 2;
      const r = 4.5 + Math.random() * 1.6;
      t.position.set(Math.cos(a) * r, 0.05, Math.sin(a) * r);
      city.add(t);
    }

    // Particles
    const particleGeo = new THREE.BufferGeometry();
    const PCOUNT = 60;
    const positions = new Float32Array(PCOUNT * 3);
    for (let i = 0; i < PCOUNT; i++) {
      positions[i * 3 + 0] = (Math.random() - 0.5) * 24;
      positions[i * 3 + 1] = Math.random() * 12;
      positions[i * 3 + 2] = (Math.random() - 0.5) * 24;
    }
    particleGeo.setAttribute('position', new THREE.BufferAttribute(positions, 3));
    const particles = new THREE.Points(particleGeo, new THREE.PointsMaterial({
      color: 0xffffff, size: 0.06, transparent: true, opacity: 0.35, depthWrite: false,
    }));
    scene.add(particles);

    // Resize
    const onResize = () => {
      const { w, h } = getSize();
      camera.aspect = w / h;
      camera.updateProjectionMatrix();
      renderer.setSize(w, h);
    };
    window.addEventListener('resize', onResize);
    let ro: ResizeObserver | null = null;
    if (options.fitToParent && canvas.parentElement && typeof ResizeObserver !== 'undefined') {
      ro = new ResizeObserver(onResize);
      ro.observe(canvas.parentElement);
    }

    // Animate
    const clock = new THREE.Clock();
    let smoothScroll = 0, smoothPX = 0, smoothPY = 0;
    let rafId = 0;
    let stopped = false;

    const tick = () => {
      if (stopped) return;
      const t = clock.getElapsedTime();
      const target = stateRef.current;
      smoothScroll += (target.scrollProgress - smoothScroll) * 0.06;
      smoothPX     += (target.pointerX      - smoothPX)     * 0.04;
      smoothPY     += (target.pointerY      - smoothPY)     * 0.04;

      city.rotation.y = t * 0.045 + smoothScroll * Math.PI * 1.4;
      city.rotation.x = -0.12 + smoothScroll * 0.18;
      city.position.y = -1.5 + Math.sin(t * 0.3) * 0.06;

      camera.position.x = smoothPX * 0.8;
      camera.position.y = 7.5 + smoothPY * 0.5 - smoothScroll * 0.8;
      camera.lookAt(0, 1.5 + Math.sin(t * 0.4) * 0.05, 0);

      particles.rotation.y = t * 0.02;
      particles.position.y = Math.sin(t * 0.25) * 0.4;

      renderer.render(scene, camera);
      rafId = requestAnimationFrame(tick);
    };
    tick();

    return () => {
      stopped = true;
      cancelAnimationFrame(rafId);
      window.removeEventListener('resize', onResize);
      if (ro) ro.disconnect();
      renderer.dispose();
      // Best-effort geometry/material disposal
      scene.traverse((obj) => {
        const m = obj as THREE.Mesh;
        if (m.geometry && typeof m.geometry.dispose === 'function') m.geometry.dispose();
        const mat = m.material;
        if (Array.isArray(mat)) mat.forEach((x) => x.dispose && x.dispose());
        else if (mat && typeof (mat as THREE.Material).dispose === 'function') (mat as THREE.Material).dispose();
      });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canvasRef]);

  return stateRef;
}
